import { memo, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BrainCore } from './BrainCore';
import type { Stage } from '../flywheel.content';

const VIEW = 600;
const C = 300;
const RING_R = 200;
const NODE_R = 33;
const HUB_R = 76;

const angleOf = (i: number) => -90 + i * 72;
const polar = (deg: number, r: number) => {
  const a = (deg * Math.PI) / 180;
  return { x: C + r * Math.cos(a), y: C + r * Math.sin(a) };
};

// Return arc: Improve (i=4) -> Capture (i=0), the segment that closes the loop.
const improveP = polar(angleOf(4), RING_R);
const captureP = polar(angleOf(0), RING_R);
const RETURN_ARC = `M ${improveP.x.toFixed(1)} ${improveP.y.toFixed(1)} A ${RING_R} ${RING_R} 0 0 1 ${captureP.x.toFixed(1)} ${captureP.y.toFixed(1)}`;

// The yellow highlight is a single segment (the Improve -> Capture arc is its home
// position) that rotates to whichever stage is active and travels the loop on a lap.
// Its rotation is driven by `highlightRotation` (degrees), set from interaction.

/* ---- flywheel disc — gently turning the whole time so it always reads as a live flywheel ---- */
const FlywheelDisc = memo(function FlywheelDisc({ reducedMotion }: { reducedMotion: boolean }) {
  const inner = 54;
  const outer = RING_R - 46;
  const content = (
    <g stroke="var(--fd-text)" strokeOpacity={0.13} fill="none">
      <circle cx={C} cy={C} r={outer} strokeWidth={1} />
      <circle cx={C} cy={C} r={(inner + outer) / 2} strokeWidth={1} />
      {Array.from({ length: 12 }, (_, i) => i * 30).map((d) => {
        const a = (d * Math.PI) / 180;
        return (
          <line
            key={d}
            x1={C + inner * Math.cos(a)}
            y1={C + inner * Math.sin(a)}
            x2={C + outer * Math.cos(a)}
            y2={C + outer * Math.sin(a)}
            strokeWidth={1}
          />
        );
      })}
    </g>
  );
  // The disc geometry is symmetric about the wheel centre, so framer's fill-box
  // origin (50% 50%) lands on the centre — no bounding-circle trick needed here.
  if (reducedMotion) return content;
  return (
    <motion.g animate={{ rotate: 360 }} transition={{ repeat: Infinity, ease: 'linear', duration: 18 }}>
      {content}
    </motion.g>
  );
});

interface WheelProps {
  stages: Stage[];
  started: boolean;
  focusedIndex: number | null;
  onFocus: (i: number) => void;
  brainFill: number;
  lapToken: number;
  boostToken: number;
  highlightRotation: number;
  lapDuration: number;
  reducedMotion: boolean;
  large?: boolean;
}

export function Wheel({
  stages,
  started,
  focusedIndex,
  onFocus,
  brainFill,
  lapToken,
  boostToken,
  highlightRotation,
  lapDuration,
  reducedMotion,
  large,
}: WheelProps) {
  const [activeLap, setActiveLap] = useState(0);
  const [activeBoost, setActiveBoost] = useState(0);

  useEffect(() => {
    if (lapToken > 0 && !reducedMotion) {
      setActiveLap(lapToken);
      const t = setTimeout(() => setActiveLap(0), lapDuration * 1000);
      return () => clearTimeout(t);
    }
  }, [lapToken, lapDuration, reducedMotion]);

  useEffect(() => {
    if (boostToken > 0) {
      setActiveBoost(boostToken);
      const t = setTimeout(() => setActiveBoost(0), 1300);
      return () => clearTimeout(t);
    }
  }, [boostToken]);

  const nodeVariants = {
    hidden: { scale: 0, opacity: 0 },
    shown: { scale: 1, opacity: 1 },
  };

  return (
    <svg viewBox={`0 0 ${VIEW} ${VIEW}`} className={`h-auto w-full ${large ? 'max-w-[600px]' : 'max-w-[480px]'}`} role="img" aria-label="The compounding loop: Capture, Assess, Build, Launch, Improve, around your Business Brain">
      <defs>
        <radialGradient id="fw-brain-glow">
          <stop offset="0%" stopColor="var(--fd-accent)" stopOpacity={0.5} />
          <stop offset="55%" stopColor="var(--fd-accent)" stopOpacity={0.12} />
          <stop offset="100%" stopColor="var(--fd-accent)" stopOpacity={0} />
        </radialGradient>
      </defs>

      {/* ---- intro treadmill (the old way) ---- */}
      <AnimatePresence>
        {!started && (
          <motion.g initial={{ opacity: 1 }} animate={{ opacity: 1 }} exit={{ opacity: 0, scale: 0.96 }} transition={{ duration: 0.5 }} style={{ transformOrigin: `${C}px ${C}px` }}>
            <rect x={C - 190} y={C - 30} width={380} height={60} rx={30} fill="var(--fd-surface-1)" stroke="var(--fd-border)" strokeWidth={1.5} />
            {!reducedMotion &&
              Array.from({ length: 6 }).map((_, i) => (
                <motion.rect
                  key={i}
                  x={C - 170}
                  y={C - 14}
                  width={5}
                  height={28}
                  rx={2.5}
                  fill="var(--fd-coral)"
                  fillOpacity={0.55}
                  initial={{ x: 0, opacity: 0 }}
                  animate={{ x: 330, opacity: [0, 1, 1, 0] }}
                  transition={{ repeat: Infinity, duration: 2.6, ease: 'linear', delay: i * 0.42 }}
                />
              ))}
            <text x={C} y={C - 50} textAnchor="middle" className="font-geist-mono" fontSize={12} letterSpacing={2} fill="var(--fd-text-subtle)">
              CONTEXT IN
            </text>
            <text x={C} y={C + 64} textAnchor="middle" className="font-geist-mono" fontSize={12} letterSpacing={2} fill="var(--fd-coral)">
              FORGOTTEN
            </text>
          </motion.g>
        )}
      </AnimatePresence>

      {/* ---- the flywheel ---- */}
      <motion.g initial={false} animate={{ opacity: started ? 1 : 0 }} transition={{ duration: 0.6, delay: started ? 0.25 : 0 }}>
        <FlywheelDisc reducedMotion={reducedMotion} />

        {/* track */}
        <motion.circle
          cx={C}
          cy={C}
          r={RING_R}
          fill="none"
          stroke="var(--fd-text)"
          strokeOpacity={0.28}
          strokeWidth={2}
          initial={{ pathLength: 0 }}
          animate={{ pathLength: started ? 1 : 0 }}
          transition={{ duration: 1.1, ease: 'easeInOut' }}
          style={{ rotate: -90, transformOrigin: `${C}px ${C}px` }}
        />

        {/* the yellow highlight — the active segment of the loop. Slides to the
            clicked stage; travels a full turn on each lap. Driven by interaction. */}
        {started && (
          <motion.g
            initial={{ rotate: 0 }}
            animate={{ rotate: highlightRotation }}
            transition={reducedMotion ? { duration: 0 } : { duration: 1.0, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* invisible full ring so framer's fill-box rotation centres on the wheel */}
            <circle cx={C} cy={C} r={RING_R} fill="none" />
            <path d={RETURN_ARC} fill="none" stroke="var(--fd-accent)" strokeWidth={15} strokeLinecap="round" opacity={0.14} />
            <motion.path
              d={RETURN_ARC}
              fill="none"
              stroke="var(--fd-accent)"
              strokeWidth={7}
              strokeLinecap="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: 0.85 }}
            />
            <circle cx={captureP.x} cy={captureP.y} r={13} fill="var(--fd-accent)" opacity={0.2} />
            <circle cx={captureP.x} cy={captureP.y} r={6.5} fill="var(--fd-accent)" />
          </motion.g>
        )}

        {/* packets into the Brain */}
        {activeLap === lapToken &&
          lapToken > 0 &&
          stages.map((_, i) => {
            const p = polar(angleOf(i), RING_R);
            return (
              <motion.circle
                key={`${lapToken}-${i}`}
                cx={p.x}
                cy={p.y}
                r={5}
                fill="var(--fd-accent)"
                initial={{ x: 0, y: 0, opacity: 0 }}
                animate={{ x: C - p.x, y: C - p.y, opacity: [0, 1, 1, 0] }}
                transition={{ duration: lapDuration * 0.78, ease: 'easeIn', delay: i * 0.05 }}
              />
            );
          })}

        {/* hub */}
        <g transform={`translate(${C} ${C})`}>
          <BrainCore fill={brainFill} reducedMotion={reducedMotion} r={HUB_R} />
        </g>
        <text x={C} y={C + HUB_R + 20} textAnchor="middle" className="font-geist-mono" fontSize={11.5} letterSpacing={1.5} fill="var(--fd-text-muted)">
          YOUR BUSINESS BRAIN
        </text>

        {/* lap pulse out of the Brain */}
        {activeLap === lapToken && lapToken > 0 && (
          <motion.circle
            key={`pulse-${lapToken}`}
            cx={C}
            cy={C}
            r={HUB_R}
            fill="var(--fd-accent)"
            initial={{ scale: 0.7, opacity: 0.35 }}
            animate={{ scale: 2.3, opacity: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            style={{ transformOrigin: `${C}px ${C}px` }}
          />
        )}

        {/* model-upgrade shockwave + flash — the whole flywheel levels up */}
        {activeBoost === boostToken && boostToken > 0 && (
          <g key={`boost-${boostToken}`}>
            <motion.circle cx={C} cy={C} fill="none" stroke="var(--fd-accent)" strokeWidth={5} initial={{ r: HUB_R, opacity: 0.95 }} animate={{ r: RING_R + 40, opacity: 0 }} transition={{ duration: 1.1, ease: 'easeOut' }} />
            <motion.circle cx={C} cy={C} fill="none" stroke="var(--fd-accent)" strokeWidth={2} initial={{ r: HUB_R, opacity: 0.7 }} animate={{ r: RING_R + 20, opacity: 0 }} transition={{ duration: 1.1, ease: 'easeOut', delay: 0.12 }} />
            <motion.circle cx={C} cy={C} r={RING_R + 44} fill="var(--fd-accent)" initial={{ opacity: 0 }} animate={{ opacity: [0, 0.15, 0] }} transition={{ duration: 0.9, ease: 'easeOut' }} />
          </g>
        )}

        {/* stage nodes */}
        <motion.g initial="hidden" animate={started ? 'shown' : 'hidden'} variants={{ shown: { transition: { staggerChildren: 0.09, delayChildren: 0.5 } } }}>
          {stages.map((s, i) => {
            const p = polar(angleOf(i), RING_R);
            const focused = focusedIndex === i;
            const lp = polar(angleOf(i), RING_R + NODE_R + 16);
            const cos = Math.cos((angleOf(i) * Math.PI) / 180);
            const anchor = cos > 0.34 ? 'start' : cos < -0.34 ? 'end' : 'middle';
            return (
              <motion.g key={s.key} variants={nodeVariants} style={{ transformOrigin: `${p.x}px ${p.y}px`, cursor: 'pointer' }} onClick={() => onFocus(i)}>
                {focused && <circle cx={p.x} cy={p.y} r={NODE_R + 7} fill="none" stroke="var(--fd-accent)" strokeWidth={1.5} strokeOpacity={0.55} />}
                <circle
                  cx={p.x}
                  cy={p.y}
                  r={NODE_R}
                  fill={focused ? 'var(--fd-accent)' : 'var(--fd-paper)'}
                  stroke="var(--fd-text)"
                  strokeWidth={1.5}
                  style={{ transition: 'fill .35s ease' }}
                />
                <text x={p.x} y={p.y - 3} textAnchor="middle" className="font-geist-mono" fontSize={13} fontWeight={700} fill="var(--fd-text)">
                  {String(i + 1).padStart(2, '0')}
                </text>
                <text x={p.x} y={p.y + 12} textAnchor="middle" className="font-geist-mono" fontSize={8} letterSpacing={0.5} fill="var(--fd-text)" fillOpacity={0.7}>
                  STEP
                </text>
                <text
                  x={lp.x}
                  y={lp.y + 4}
                  textAnchor={anchor}
                  className="font-geist"
                  fontSize={15}
                  fontWeight={600}
                  fill={focused ? 'var(--fd-text)' : 'var(--fd-text-muted)'}
                  style={{ transition: 'fill .35s ease' }}
                >
                  {s.tag}
                </text>
              </motion.g>
            );
          })}
        </motion.g>
      </motion.g>
    </svg>
  );
}
